import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaChartBar, FaUsers, FaEnvelope, FaUserEdit, FaUserShield, 
  FaDesktop, FaServer, FaCog, FaChartLine, FaHeartbeat, FaBell, FaBuilding, FaMapMarkerAlt, FaFileInvoiceDollar, FaCogs, FaTachometerAlt,
  FaChevronLeft, FaChevronRight, FaShieldAlt // Added shield icon for Admin Panel
} from 'react-icons/fa'; 
import './AdminSidebar.css'; 

// Accept props for collapsed state and toggle function
const AdminSidebar = ({ collapsed, toggleSidebar }) => { 
  const location = useLocation();
  
  // Force recalculation of sidebar position when route changes
  useEffect(() => {
    // Update CSS variable for header height to ensure proper sidebar positioning
    const updateHeaderHeight = () => {
      const headerHeight = document.querySelector('.admin-header-container')?.offsetHeight || 132;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    };
    
    // Call immediately and after a short delay to ensure DOM is fully rendered
    updateHeaderHeight();
    const timeoutId = setTimeout(updateHeaderHeight, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Define navigation items with more specific icons (no changes needed here)
  const dashboardItem = { 
    key: 'home',
    label: 'Dashboard', 
    icon: <FaTachometerAlt className="sidebar-icon" />, 
    path: '/dashboard/home' 
  };

  const adminPanelItems = [
    { key: 'users', label: 'Users', icon: <FaUsers className="sidebar-icon" />, path: '/dashboard/users' },
    { key: 'contacts', label: 'Contacts', icon: <FaEnvelope className="sidebar-icon" />, path: '/dashboard/contacts' },
    { key: 'organization', label: 'Organizations', icon: <FaBuilding className="sidebar-icon" />, path: '/dashboard/organization' },
    { key: 'location', label: 'Locations', icon: <FaMapMarkerAlt className="sidebar-icon" />, path: '/dashboard/location' },
    { key: 'devices', label: 'Devices', icon: <FaDesktop className="sidebar-icon" />, path: '/dashboard/devices' },
    { key: 'services', label: 'Services', icon: <FaServer className="sidebar-icon" />, path: '/dashboard/services' },
    { key: 'billing', label: 'Billing', icon: <FaFileInvoiceDollar className="sidebar-icon" />, path: '/dashboard/billing' },
    { key: 'analytics', label: 'Analytics', icon: <FaChartLine className="sidebar-icon" />, path: '/dashboard/analytics' },
    { key: 'system', label: 'System', icon: <FaHeartbeat className="sidebar-icon" />, path: '/dashboard/system' },
    { key: 'settings', label: 'Settings', icon: <FaCogs className="sidebar-icon" />, path: '/dashboard/settings' }
  ];

  return (
    // Apply 'collapsed' class based on prop
    <div className={`admin-sidebar ${collapsed ? 'collapsed' : ''}`}> 
      {/* Sidebar Header with Icon */}
      <div className="admin-sidebar-header">
         <FaShieldAlt className="admin-logo" /> {/* Admin icon that stays visible when collapsed */}
         <h2>Admin Panel</h2>
      </div>

      {/* All Navigation Links in a single nav element */}
      <nav className="admin-nav">
        {/* Dashboard Link */}
        <Link 
          key={dashboardItem.key}
          className={`admin-nav-item ${location.pathname === dashboardItem.path ? 'active' : ''}`} 
          to={dashboardItem.path}
          title={dashboardItem.label} // Tooltip for collapsed view
        >
          {dashboardItem.icon}
          <span className="nav-item-label">{dashboardItem.label}</span>
        </Link>
        
        {/* Divider */}
        <hr className="admin-sidebar-divider" />
        
        {/* Admin Panel Navigation Links */}
        {adminPanelItems.map(item => (
          <Link 
            key={item.key} 
            className={`admin-nav-item ${location.pathname === item.path ? 'active' : ''}`} 
            to={item.path}
            title={item.label} // Tooltip for collapsed view
          >
            {item.icon}
            <span className="nav-item-label">{item.label}</span>
          </Link>
        ))}
      </nav>

      {/* Sidebar Footer with Toggle Button */}
      <div className="admin-sidebar-footer">
        <button 
          onClick={toggleSidebar} 
          className="sidebar-toggle-button"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {/* Use arrow icons that indicate direction */}
          {collapsed ? <FaChevronRight className="toggle-icon" /> : <FaChevronLeft className="toggle-icon" />}
        </button>
      </div>
    </div>
  );
};

export default AdminSidebar;
